import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard, Download, Sparkles, Tv,
  RefreshCw, Package, HardDrive, Store, AlertCircle, CheckCircle2, ArrowRight, Rocket,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { AlertModal } from '@/components/ui/AlertModal';
import { apiClient } from '@/services/api';
import { formatCents, formatDate, cn } from '@/utils/cn';
import type { Plan, BillingCycle } from '@/types/plan';
import { ChangePlanModal } from './ChangePlanModal';
import { AdjustTvsModal } from './AdjustTvsModal';
import { ActivateSubscriptionModal } from './ActivateSubscriptionModal';
import { PayInvoiceModal } from './PayInvoiceModal';

// Subscription do backend (mesma forma de Plan, mas inline pra evitar tipo separado)
// JSON snake_case por causa da config do Program.cs.
interface Subscription {
  id: number;
  tenant_id: number;
  plan_id: number;
  plan?: Plan;
  status: 'trialing' | 'active' | 'past_due' | 'paused' | 'cancelled';
  billing_cycle: BillingCycle;
  tv_count: number;
  base_first_tv_cents: number;
  base_price_per_tv_cents: number;
  subtotal_cents: number;
  total_cents: number;
  volume_discount_cents: number;
  mycommerce_discount_cents: number;
  coupon_discount_cents: number;
  trial_end?: string | null;
  current_period_start: string;
  current_period_end: string;
  next_billing_date?: string | null;
  payment_method?: string | null;
}

interface Invoice {
  id: number;
  invoice_number: string;
  period_start: string;
  period_end: string;
  due_date: string;
  paid_at?: string | null;
  total_cents: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  payment_method?: string | null;
  asaas_invoice_url?: string | null;
  nfe_url?: string | null;
}

const STATUS_VARIANT: Record<Subscription['status'], 'default' | 'success' | 'warning' | 'danger' | 'muted'> = {
  trialing: 'default',
  active: 'success',
  past_due: 'warning',
  paused: 'muted',
  cancelled: 'danger',
};

const STATUS_LABEL: Record<Subscription['status'], string> = {
  trialing: 'Em trial',
  active: 'Ativo',
  past_due: 'Pagamento em atraso',
  paused: 'Pausado',
  cancelled: 'Cancelado',
};

const INVOICE_STATUS_VARIANT: Record<Invoice['status'], 'default' | 'success' | 'warning' | 'danger' | 'muted'> = {
  pending: 'warning',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'muted',
  refunded: 'muted',
};

const INVOICE_STATUS_LABEL: Record<Invoice['status'], string> = {
  pending: 'Aguardando',
  paid: 'Paga',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
  refunded: 'Estornada',
};

export function BillingPage() {
  const qc = useQueryClient();
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [adjustTvsOpen, setAdjustTvsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [syncingInvoiceId, setSyncingInvoiceId] = useState<number | null>(null);
  const [syncResult, setSyncResult] = useState<{ variant: 'success' | 'error' | 'info'; title: string; description: string } | null>(null);

  const { data: sub, isLoading: subLoading } = useQuery<Subscription>({
    queryKey: ['subscription'],
    queryFn: () => apiClient.getSubscription().then(r => r.data as Subscription),
  });
  const { data: invoices = [] } = useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => apiClient.getInvoices().then(r => r.data as Invoice[]),
  });
  const { data: tvStats } = useQuery<{ total: number; online: number; offline: number }>({
    queryKey: ['tvs-stats'],
    queryFn: () => apiClient.getTvsStats().then(r => r.data),
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiClient.cancelSubscription('Cancelamento solicitado pelo cliente'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  });

  async function handleSyncInvoice(inv: Invoice) {
    setSyncingInvoiceId(inv.id);
    try {
      const { data } = await apiClient.syncInvoiceStatus(inv.id) as any;
      await qc.invalidateQueries({ queryKey: ['invoices'] });
      await qc.invalidateQueries({ queryKey: ['subscription'] });
      if (data.status === 'paid') {
        setSyncResult({
          variant: 'success',
          title: 'Pagamento confirmado!',
          description: 'A fatura foi baixada com sucesso. Seu acesso está garantido.',
        });
      } else if (data.synced && data.status !== data.previous_status) {
        setSyncResult({
          variant: 'info',
          title: 'Status atualizado',
          description: `A fatura foi atualizada para "${INVOICE_STATUS_LABEL[data.status as Invoice['status']] ?? data.status}".`,
        });
      }
      // Se não houve alteração, não exibe nada — o botão girou e atualizou silenciosamente
    } catch {
      setSyncResult({
        variant: 'error',
        title: 'Erro ao consultar Asaas',
        description: 'Não foi possível verificar o pagamento agora. Tente novamente em instantes.',
      });
    } finally {
      setSyncingInvoiceId(null);
    }
  }

  if (subLoading || !sub) {
    return (
      <div className="container-app py-8">
        <div className="text-center text-muted-foreground py-12">Carregando assinatura…</div>
      </div>
    );
  }

  const plan = sub.plan;
  const isTrialing = sub.status === 'trialing';
  const isPastDue = sub.status === 'past_due';
  const isCancelled = sub.status === 'cancelled';
  const additionalCount = Math.max(0, sub.tv_count - 1);
  const additionalTotal = additionalCount * sub.base_price_per_tv_cents;

  // Anual seria? (preview de economia caso esteja no mensal)
  const annualSavings = sub.billing_cycle === 'monthly' && plan
    ? (sub.total_cents * 12) - (plan.first_tv_annual_cents + additionalCount * plan.price_per_tv_annual_cents)
    : 0;

  // Faturas agrupadas por status
  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const pendingCount = invoices.filter(i => i.status === 'pending').length;
  const overdueCount = invoices.filter(i => i.status === 'overdue').length;

  return (
    <div className="container-app py-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <CreditCard className="h-7 w-7" />
            Cobrança
          </h1>
          <p className="text-muted-foreground mt-1">Gerencie sua assinatura, plano e faturas</p>
        </div>
        <Badge variant={STATUS_VARIANT[sub.status]} className="text-sm px-3 py-1">
          {STATUS_LABEL[sub.status]}
        </Badge>
      </div>

      {/* CTA de ativação — destaque máximo quando em trial */}
      {isTrialing && plan && (
        <div className="relative mt-6 rounded-2xl overflow-hidden shadow-glow">
          <div className="gradient-brand p-6 md:p-8 text-white relative">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'radial-gradient(600px 200px at 20% 0%, rgba(255,255,255,.35), transparent), radial-gradient(400px 200px at 80% 100%, rgba(255,255,255,.25), transparent)',
            }} />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-white/15 border border-white/30 backdrop-blur text-white text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-3">
                  <Sparkles className="h-3 w-3" /> Você está em trial
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                  Ative seu plano e mantenha tudo rodando.
                </h2>
                <p className="text-white/90 mt-2 text-sm md:text-base">
                  Use o cupom <code className="bg-white/20 px-1.5 py-0.5 rounded font-mono">LANCAMENTO50</code> e ganhe
                  {' '}<strong>50% OFF nos 3 primeiros meses</strong>. Pague apenas {formatCents(Math.round(sub.total_cents * 0.5))} no 1º mês.
                </p>
              </div>
              <Button
                size="lg"
                variant="secondary"
                className="h-12 px-7 bg-white text-brand-700 hover:bg-white/95 shadow-elev font-semibold"
                onClick={() => setActivateOpen(true)}
              >
                <Rocket className="h-4 w-4" />
                Ativar plano agora
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de status crítico */}
      {isPastDue && (() => {
        // Fatura overdue mais antiga vira o foco do CTA.
        const oldestOverdue = invoices
          .filter(i => i.status === 'overdue')
          .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
        return (
          <div className="mt-6 rounded-lg border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3 flex-wrap">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-red-800">Sua fatura está em atraso</div>
              <div className="text-sm text-red-700 mt-0.5">
                Pague agora pra evitar suspensão do serviço. Você tem {overdueCount} fatura{overdueCount > 1 ? 's' : ''} vencida{overdueCount > 1 ? 's' : ''}
                {oldestOverdue && <> · A mais antiga venceu em <strong>{formatDate(oldestOverdue.due_date)}</strong></>}.
              </div>
            </div>
            {oldestOverdue && (
              <Button variant="destructive" size="sm" onClick={() => setPayingInvoice(oldestOverdue)}>
                Pagar agora ({formatCents(oldestOverdue.total_cents)})
              </Button>
            )}
          </div>
        );
      })()}

      {isCancelled && (
        <div className="mt-6 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-800">Assinatura cancelada</div>
            <div className="text-sm text-amber-700 mt-0.5">
              Você ainda pode usar o serviço até o fim do período atual. Para reativar, escolha um plano abaixo.
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Plano atual (col-span-2) */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm text-muted-foreground">Plano atual</div>
                <div className="text-3xl font-bold mt-1">{plan?.name ?? '—'}</div>
                <div className="text-sm text-muted-foreground mt-1">{plan?.description}</div>
                <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-3">
                  <span><Tv className="inline h-3 w-3 mr-1" />{sub.tv_count} TV{sub.tv_count > 1 ? 's' : ''}</span>
                  <span><RefreshCw className="inline h-3 w-3 mr-1" />{sub.billing_cycle === 'monthly' ? 'Mensal' : 'Anual'}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total {sub.billing_cycle === 'monthly' ? 'mensal' : 'anual equiv.'}</div>
                <div className="text-4xl font-bold bg-gradient-to-r from-brand-600 to-accent bg-clip-text text-transparent">
                  {formatCents(sub.total_cents)}
                </div>
                {sub.billing_cycle === 'monthly' && annualSavings > 0 && (
                  <div className="text-xs text-emerald-600 mt-1">
                    💰 Anual: economia de {formatCents(annualSavings)}/ano
                  </div>
                )}
              </div>
            </div>

            {/* Breakdown: 1ª TV + adicionais */}
            <div className="mt-6 rounded-lg bg-muted/40 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">1ª TV (taxa base do plano)</span>
                <span className="font-medium">{formatCents(sub.base_first_tv_cents)}</span>
              </div>
              {additionalCount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{additionalCount}× TV adicional</span>
                  <span className="font-medium">{formatCents(additionalTotal)}</span>
                </div>
              )}
              {sub.volume_discount_cents > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Desconto de volume</span>
                  <span>− {formatCents(sub.volume_discount_cents)}</span>
                </div>
              )}
              {sub.mycommerce_discount_cents > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Desconto MyCommerce</span>
                  <span>− {formatCents(sub.mycommerce_discount_cents)}</span>
                </div>
              )}
              {sub.coupon_discount_cents > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Cupom</span>
                  <span>− {formatCents(sub.coupon_discount_cents)}</span>
                </div>
              )}
              <div className="border-t border-border pt-2 flex justify-between items-baseline">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold">{formatCents(sub.total_cents)}</span>
              </div>
            </div>

            {/* Períodos */}
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              {isTrialing && sub.trial_end && (
                <div className="rounded-md border border-border p-3">
                  <div className="text-xs text-muted-foreground">Trial termina em</div>
                  <div className="font-semibold mt-0.5">{formatDate(sub.trial_end)}</div>
                </div>
              )}
              <div className="rounded-md border border-border p-3">
                <div className="text-xs text-muted-foreground">Período atual</div>
                <div className="font-semibold mt-0.5">
                  {formatDate(sub.current_period_start)} → {formatDate(sub.current_period_end)}
                </div>
              </div>
              {!isTrialing && sub.next_billing_date && (
                <div className="rounded-md border border-border p-3">
                  <div className="text-xs text-muted-foreground">Próxima cobrança</div>
                  <div className="font-semibold mt-0.5">{formatDate(sub.next_billing_date)}</div>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="mt-6 flex gap-2 flex-wrap">
              {isCancelled ? (
                <Button variant="gradient" className="btn-gradient-glow" onClick={() => setActivateOpen(true)}>
                  <Rocket className="h-4 w-4" /> Reativar plano
                </Button>
              ) : (
                <>
                  <Button variant="default" onClick={() => setChangePlanOpen(true)}>
                    Mudar plano
                  </Button>
                  <Button variant="outline" onClick={() => setAdjustTvsOpen(true)}>
                    <Tv className="h-4 w-4" /> Ajustar TVs
                  </Button>
                  <Button variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto" onClick={() => setCancelOpen(true)}>
                    Cancelar assinatura
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Coluna lateral: resumo + uso + cupom */}
        <div className="space-y-6">
          {/* Resumo de faturas */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Resumo de faturas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />Pagas</span>
                <span className="font-bold text-lg">{paidCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Pendentes</span>
                <span className={cn('font-bold text-lg', pendingCount > 0 && 'text-amber-600')}>{pendingCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Atrasadas</span>
                <span className={cn('font-bold text-lg', overdueCount > 0 && 'text-red-600')}>{overdueCount}</span>
              </div>
            </CardContent>
          </Card>

          {/* Uso */}
          {plan && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Uso do plano</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* TVs */}
                <UsageBar
                  icon={<Tv className="h-3.5 w-3.5" />}
                  label="TVs ativas"
                  used={tvStats?.total ?? 0}
                  max={plan.max_tvs}
                  unit="TVs"
                />
                {/* Produtos */}
                <UsageBar
                  icon={<Package className="h-3.5 w-3.5" />}
                  label="Produtos"
                  used={null}
                  max={plan.max_products}
                  unit="produtos"
                />
                {/* Storage */}
                <UsageBar
                  icon={<HardDrive className="h-3.5 w-3.5" />}
                  label="Mídia"
                  used={null}
                  max={plan.max_storage_mb}
                  unit="MB"
                  formatter={(v) => `${(v / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`}
                />
                {/* Lojas */}
                <UsageBar
                  icon={<Store className="h-3.5 w-3.5" />}
                  label="Lojas"
                  used={null}
                  max={plan.max_stores}
                  unit="lojas"
                />
              </CardContent>
            </Card>
          )}

          {/* Promo de lançamento (se trial e elegível) */}
          {isTrialing && (
            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-orange-600" />
                  <div className="font-bold text-orange-700">Promo de lançamento</div>
                </div>
                <p className="text-sm text-orange-700 mb-3">
                  Use o cupom <code className="bg-white px-1.5 py-0.5 rounded text-orange-700 font-mono text-xs">LANCAMENTO50</code> ao ativar
                  e ganhe <strong>50% OFF nos 3 primeiros meses</strong>.
                </p>
                <div className="text-xs text-orange-600">
                  Vagas limitadas aos 50 primeiros clientes.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Histórico de faturas */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Histórico de faturas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              {isTrialing
                ? 'Nenhuma fatura ainda — você está em trial.'
                : 'Nenhuma fatura emitida.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left">
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Número</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Período</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Vencimento</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase text-right">Total</th>
                    <th className="p-3 text-xs font-semibold text-muted-foreground uppercase text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {formatDate(inv.period_start)} → {formatDate(inv.period_end)}
                      </td>
                      <td className="p-3 text-xs">{formatDate(inv.due_date)}</td>
                      <td className="p-3">
                        <Badge variant={INVOICE_STATUS_VARIANT[inv.status]}>
                          {INVOICE_STATUS_LABEL[inv.status]}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-semibold">{formatCents(inv.total_cents)}</td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          {(inv.status === 'pending' || inv.status === 'overdue') && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Verificar pagamento no Asaas"
                                disabled={syncingInvoiceId === inv.id}
                                onClick={() => handleSyncInvoice(inv)}
                              >
                                <RefreshCw className={cn('h-4 w-4', syncingInvoiceId === inv.id && 'animate-spin')} />
                              </Button>
                              <Button size="sm" variant={inv.status === 'overdue' ? 'destructive' : 'default'} onClick={() => setPayingInvoice(inv)}>
                                Pagar
                              </Button>
                            </>
                          )}
                          {inv.status === 'paid' && inv.nfe_url && (
                            <Button asChild size="sm" variant="ghost" title="Baixar NF-e">
                              <a href={inv.nfe_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modais */}
      {plan && (
        <>
          <ChangePlanModal
            open={changePlanOpen}
            onOpenChange={setChangePlanOpen}
            currentPlanCode={plan.code}
            currentTvCount={sub.tv_count}
            currentBillingCycle={sub.billing_cycle}
            isTrialing={isTrialing}
          />
          <AdjustTvsModal
            open={adjustTvsOpen}
            onOpenChange={setAdjustTvsOpen}
            plan={plan}
            currentTvCount={sub.tv_count}
            currentBillingCycle={sub.billing_cycle}
            isTrialing={isTrialing}
          />
          <ActivateSubscriptionModal
            open={activateOpen}
            onOpenChange={setActivateOpen}
            plan={plan}
            tvCount={sub.tv_count}
            billingCycle={sub.billing_cycle}
            allowEdit={isCancelled}
          />
        </>
      )}

      {payingInvoice && (
        <PayInvoiceModal
          open={!!payingInvoice}
          onOpenChange={(o) => { if (!o) setPayingInvoice(null); }}
          invoice={payingInvoice}
        />
      )}

      {syncResult && (
        <AlertModal
          open={!!syncResult}
          onOpenChange={(o) => { if (!o) setSyncResult(null); }}
          variant={syncResult.variant}
          title={syncResult.title}
          description={syncResult.description}
        />
      )}

      <AlertModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        variant="warning"
        title="Cancelar assinatura?"
        description={
          isTrialing
            ? 'Você está em trial — o cancelamento só impede a ativação automática. Você continua usando o serviço até o fim do período de teste.'
            : `Sua assinatura será cancelada. Você continua usando o serviço até ${formatDate(sub.current_period_end)} e não será cobrado novamente. Tem certeza?`
        }
        confirmLabel="Sim, cancelar"
        onConfirm={() => cancelMutation.mutate()}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// UsageBar: barra de uso com fallback gracioso quando "used" é desconhecido.
// Quando used=null, mostra só o limite ("Até X").
// -----------------------------------------------------------------------
interface UsageBarProps {
  icon: React.ReactNode;
  label: string;
  used: number | null;
  max: number | null;
  unit: string;
  formatter?: (v: number) => string;
}

function UsageBar({ icon, label, used, max, unit, formatter }: UsageBarProps) {
  const fmt = formatter ?? ((v: number) => `${v.toLocaleString('pt-BR')} ${unit}`);
  const isUnlimited = max == null;
  const pct = !isUnlimited && used != null && max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const warn = pct >= 80;

  return (
    <div>
      <div className="flex justify-between items-center text-xs mb-1">
        <span className="text-muted-foreground flex items-center gap-1.5">{icon}{label}</span>
        <span className={cn('font-medium', warn && 'text-amber-600')}>
          {used != null ? fmt(used) : '—'}
          {' / '}
          {isUnlimited ? 'ilimitado' : fmt(max)}
        </span>
      </div>
      {!isUnlimited && used != null && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={cn('h-full transition-all', warn ? 'bg-amber-500' : 'bg-brand-600')}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
